import React, { useEffect, useRef, useState } from "react";
import { withRouter } from "react-router-dom";
import Pusher from "pusher-js";
import config from "../../config";
import { Logout, getProfileNotifications } from "../../api/index.js";
import BurgerIcon from "../assets/svg/icons/common/burger.jsx";
import EMSBootModal from "../../components/modals/add-new-application-modal/ems-boot-application-modal";
import FlagCA from "../assets/svg/icons/common/FlagCA.jsx";
import FlagUSA from "../assets/svg/icons/common/FlagUSA.jsx";
import PCLogoFull from "../assets/svg/icons/common/PCLogoFull.jsx";
import Avatar from "../components/Avatar/Avatar.jsx";
import ButtonIcon from "../components/ButtonIcon/ButtonIcon.jsx";
import Button from "../components/new-button";
import NotificationDropdown from "../components/NotificationDropdown/NotificationDropdown.jsx";
import useCommonStore from "../stores/common.js";
import useDomainStore from "../stores/domain.js";
import useUserStore from "../stores/user.js";
import NewPasswordModal from "./components/new-password";
import PersonalDetailsModal from "./components/personal-details";
import ResetPasswordModal from "./components/reset-password";
import ApiTokensModal from "./components/api-tokens";
import TwoFactorModal from "./components/two-factor-auth";
import { useOrganizationLogo } from "../hooks/general/useOrganizationLogo";
import style from "./Header.module.scss";

const Header = ({ history }) => {
  const loggedInUser = useUserStore((state) => state.user);
  const { isKurvDomain, initializeDomain } = useDomainStore();
  const [dropDownOpen, setDropDownOpen] = useState(false);
  const dropDownRef = useRef(null);
  const [emsBootModal, setEmsBootModal] = useState(false);
  const [userDropDownOpen, setUserDropDownOpen] = useState(false);
  const userDropDownRef = useRef(null);
  const [notificationDropDownOpen, setNotificationDropDownOpen] = useState(false);
  const notificationDropDownRef = useRef(null);
  const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);
  const [notificationRefreshTrigger, setNotificationRefreshTrigger] = useState(0);
  const toggleMobileMenu = useCommonStore((state) => state.toggleMobileMenu);
  const { orgLogoFull } = useOrganizationLogo();
  // Initialize domain detection
  useEffect(() => {
    initializeDomain();
  }, [initializeDomain]);

  // Reset Password Modal
  const resetPasswordModal = useUserStore((state) => state.resetPasswordModal);
  const setResetPasswordModal = useUserStore(
    (state) => state.setResetPasswordModal,
  );

  const newPasswordModal = useUserStore((state) => state.newPasswordModal);
  const setNewPasswordModal = useUserStore(
    (state) => state.setNewPasswordModal,
  );

  const personalDetailsModal = useUserStore(
    (state) => state.personalDetailsModal,
  );
  const setPersonalDetailsModal = useUserStore(
    (state) => state.setPersonalDetailsModal,
  );

  const apiTokensModal = useUserStore((state) => state.apiTokensModal);
  const setApiTokensModal = useUserStore((state) => state.setApiTokensModal);

  const twoFactorModal = useUserStore((state) => state.twoFactorModal);
  const setTwoFactorModal = useUserStore((state) => state.setTwoFactorModal);

  const toCreateApp = (country) => {
    history.push(
      `/create-app/${country}`,
    );
    setDropDownOpen(false);
  };

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const userId = loggedInUser?.id;
    if (!userId || !config?.paymentcloud?.PUSHER_KEY) {
      return undefined;
    }

    const pusher = new Pusher(config.paymentcloud.PUSHER_KEY, {
      cluster: config.paymentcloud.PUSHER_CLUSTER,
      encrypted: true,
      authEndpoint: "/broadcasting/auth",
      auth: {
        headers: {
          "STRATUS-XSRF-TOKEN": document
            .querySelector('meta[name="stratus-csrf-token"]')
            ?.getAttribute("content"),
        },
      },
    });

    const channelName = `private-notifications_${userId}`;
    const channel = pusher.subscribe(channelName);

    channel.bind("notification.created", () => {
      setHasUnreadNotifications(true);
      setNotificationRefreshTrigger((prev) => prev + 1);
    });

    channel.bind("pusher:subscription_error", (err) => {
      console.error(`Failed to subscribe to ${channelName}`, err);
    });

    return () => {
      try {
        pusher.unsubscribe(channelName);
        pusher.disconnect();
      } catch (err) {
        console.error("Error tearing down notifications Pusher", err);
      }
    };
  }, [loggedInUser?.id]);

  const fetchUnreadCount = async () => {
    try {
      const response = await getProfileNotifications({ page: 1 });
      if (response?.success && response?.data) {
        const unreadCount = response.data.data?.filter((n) => !n.read).length || 0;
        setHasUnreadNotifications(unreadCount > 0);
      } else if (response?.status && response?.data) {
        const unreadCount = response.data.data?.filter((n) => !n.read).length || 0;
        setHasUnreadNotifications(unreadCount > 0);
      }
    } catch (error) {
      console.error("Error fetching unread notifications:", error);
    }
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropDownRef.current && !dropDownRef.current.contains(event.target)) {
        setDropDownOpen(false);
      }
      if (
        userDropDownRef.current &&
        !userDropDownRef.current.contains(event.target)
      ) {
        setUserDropDownOpen(false);
      }
      if (
        notificationDropDownRef.current &&
        !notificationDropDownRef.current.contains(event.target)
      ) {
        setNotificationDropDownOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <header
      className={`${style.header} ${isKurvDomain ? style.kurv_theme : ""}`}
    >
      <div className={style.headerLeftNav}>
        <button className={style.burgerMenu} onClick={() => toggleMobileMenu()}>
          <BurgerIcon />
        </button>

        <span className={style.headerLeftNavLogoContainer}>
          {orgLogoFull ? (
            <img
              src={orgLogoFull}
              alt="Logo"
              style={{ maxHeight: "30px", objectFit: "contain" }}
            />
          ) : (
            <PCLogoFull />
          )}
        </span>
      </div>

      <nav className={style.header_nav}>        
        {loggedInUser?.is_v2_enable
         ? (
          <>
            <div ref={dropDownRef}>
              <ButtonIcon
                bgColor={isKurvDomain ? "#22A276" : "#4C70EF"}
                textColor="#FFF"
                onClick={() => {
                  history.push("/create-app/usa");
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g id="Plus">
                    <path
                      id="Vector"
                      d="M14 8C14 8.13261 13.9473 8.25979 13.8536 8.35355C13.7598 8.44732 13.6326 8.5 13.5 8.5H8.5V13.5C8.5 13.6326 8.44732 13.7598 8.35355 13.8536C8.25979 13.9473 8.13261 14 8 14C7.86739 14 7.74021 13.9473 7.64645 13.8536C7.55268 13.7598 7.5 13.6326 7.5 13.5V8.5H2.5C2.36739 8.5 2.24021 8.44732 2.14645 8.35355C2.05268 8.25979 2 8.13261 2 8C2 7.86739 2.05268 7.74021 2.14645 7.64645C2.24021 7.55268 2.36739 7.5 2.5 7.5H7.5V2.5C7.5 2.36739 7.55268 2.24021 7.64645 2.14645C7.74021 2.05268 7.86739 2 8 2C8.13261 2 8.25979 2.05268 8.35355 2.14645C8.44732 2.24021 8.5 2.36739 8.5 2.5V7.5H13.5C13.6326 7.5 13.7598 7.55268 13.8536 7.64645C13.9473 7.74021 14 7.86739 14 8Z"
                      fill="white"
                    />
                  </g>
                </svg>
                <span>Create</span>
              </ButtonIcon>

              {dropDownOpen && (
                <div className={style.merchant_btn_dropdown}>
                  <div className={style.drop_heading}>
                    <span className={style.merchant_drop_text}>
                      Merchant country
                    </span>
                  </div>
                  <div
                    className={style.drop_item}
                    onClick={() => toCreateApp("usa")}
                  >
                    <FlagUSA />
                    <span>USA</span>
                  </div>
                  <div
                    className={style.drop_item}
                    onClick={() => toCreateApp("ca")}
                  >
                    <FlagCA />
                    <span>Canada</span>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <Button
            variant={"primary"}
            onClick={() => setEmsBootModal(true)}
            icon={
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g id="Plus">
                  <path
                    id="Vector"
                    d="M14 8C14 8.13261 13.9473 8.25979 13.8536 8.35355C13.7598 8.44732 13.6326 8.5 13.5 8.5H8.5V13.5C8.5 13.6326 8.44732 13.7598 8.35355 13.8536C8.25979 13.9473 8.13261 14 8 14C7.86739 14 7.74021 13.9473 7.64645 13.8536C7.55268 13.7598 7.5 13.6326 7.5 13.5V8.5H2.5C2.36739 8.5 2.24021 8.44732 2.14645 8.35355C2.05268 8.25979 2 8.13261 2 8C2 7.86739 2.05268 7.74021 2.14645 7.64645C2.24021 7.55268 2.36739 7.5 2.5 7.5H7.5V2.5C7.5 2.36739 7.55268 2.24021 7.64645 2.14645C7.74021 2.05268 7.86739 2 8 2C8.13261 2 8.25979 2.05268 8.35355 2.14645C8.44732 2.24021 8.5 2.36739 8.5 2.5V7.5H13.5C13.6326 7.5 13.7598 7.55268 13.8536 7.64645C13.9473 7.74021 14 7.86739 14 8Z"
                    fill="white"
                  />
                </g>
              </svg>
            }
          >
            Create
          </Button>
        )}
        <div className={style.notification_container} ref={notificationDropDownRef}>
          <ButtonIcon onClick={() => setNotificationDropDownOpen(!notificationDropDownOpen)}>
            <div className={style.notification_icon_wrapper}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M15.7499 21C15.7499 21.1989 15.6709 21.3897 15.5303 21.5303C15.3896 21.671 15.1988 21.75 14.9999 21.75H8.99993C8.80102 21.75 8.61025 21.671 8.4696 21.5303C8.32895 21.3897 8.24993 21.1989 8.24993 21C8.24993 20.8011 8.32895 20.6103 8.4696 20.4697C8.61025 20.329 8.80102 20.25 8.99993 20.25H14.9999C15.1988 20.25 15.3896 20.329 15.5303 20.4697C15.6709 20.6103 15.7499 20.8011 15.7499 21ZM20.7984 18C20.6682 18.2292 20.4793 18.4196 20.2511 18.5514C20.0228 18.6833 19.7635 18.7518 19.4999 18.75H4.49993C4.23624 18.7496 3.97731 18.6798 3.74923 18.5475C3.52115 18.4151 3.33198 18.225 3.20079 17.9963C3.0696 17.7676 3.00102 17.5083 3.00196 17.2446C3.00291 16.9809 3.07335 16.7222 3.20618 16.4944C3.72649 15.5981 4.49993 13.0631 4.49993 9.75C4.49993 7.76088 5.29011 5.85322 6.69663 4.4467C8.10315 3.04018 10.0108 2.25 11.9999 2.25C13.9891 2.25 15.8967 3.04018 17.3032 4.4467C18.7098 5.85322 19.4999 7.76088 19.4999 9.75C19.4999 13.0622 20.2743 15.5981 20.7946 16.4944C20.9288 16.7225 20.9997 16.9822 21.0002 17.2468C21.0007 17.5114 20.9307 17.7714 20.7974 18H20.7984ZM19.4999 17.25C18.7752 16.0059 17.9999 13.1297 17.9999 9.75C17.9999 8.1587 17.3678 6.63258 16.2426 5.50736C15.1174 4.38214 13.5912 3.75 11.9999 3.75C10.4086 3.75 8.88251 4.38214 7.75729 5.50736C6.63207 6.63258 5.99993 8.1587 5.99993 9.75C5.99993 13.1306 5.22368 16.0069 4.49993 17.25H19.4999Z"
                  fill="#3A383F"
                />
              </svg>
              {hasUnreadNotifications && (
                <span className={style.notification_dot}></span>
              )}
            </div>
          </ButtonIcon>

          {notificationDropDownOpen && (
            <NotificationDropdown
              history={history}
              onClose={() => setNotificationDropDownOpen(false)}
              onNotificationsUpdate={fetchUnreadCount}
              refreshTrigger={notificationRefreshTrigger}
            />
          )}
        </div>
        {/* <ButtonIcon>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M12.0002 2.25C10.3169 2.24963 8.66213 2.68508 7.19704 3.51396C5.73194 4.34285 4.50641 5.53692 3.63971 6.97997C2.77302 8.42301 2.29469 10.0659 2.25129 11.7486C2.20789 13.4314 2.60089 15.0967 3.39205 16.5825L2.32799 19.7747C2.23985 20.039 2.22706 20.3226 2.29105 20.5938C2.35504 20.8649 2.49328 21.1129 2.69029 21.3099C2.88729 21.5069 3.13526 21.6451 3.40642 21.7091C3.67757 21.7731 3.96119 21.7603 4.22549 21.6722L7.41768 20.6081C8.72527 21.3036 10.1741 21.6921 11.6543 21.744C13.1344 21.7959 14.6069 21.51 15.9601 20.9079C17.3132 20.3057 18.5114 19.4033 19.4636 18.269C20.4159 17.1346 21.0972 15.7983 21.4559 14.3613C21.8146 12.9244 21.8412 11.4246 21.5337 9.97578C21.2263 8.52701 20.5928 7.16732 19.6813 5.99992C18.7699 4.83253 17.6045 3.88811 16.2736 3.23836C14.9427 2.58861 13.4812 2.25061 12.0002 2.25ZM12.0002 20.25C10.5498 20.251 9.12495 19.8691 7.86955 19.1428C7.77763 19.0895 7.67544 19.0563 7.56974 19.0455C7.46404 19.0347 7.35724 19.0464 7.25643 19.08L3.75018 20.25L4.91924 16.7438C4.95294 16.643 4.96485 16.5362 4.95418 16.4305C4.94351 16.3248 4.91051 16.2226 4.85736 16.1306C3.94796 14.5584 3.58284 12.7299 3.81864 10.929C4.05444 9.12801 4.87798 7.4552 6.16151 6.17006C7.44503 4.88491 9.11679 4.05925 10.9175 3.82118C12.7181 3.5831 14.547 3.94591 16.1204 4.85333C17.6938 5.76074 18.9238 7.16204 19.6196 8.83983C20.3153 10.5176 20.4379 12.3781 19.9684 14.1327C19.4989 15.8873 18.4634 17.4379 17.0227 18.5439C15.582 19.65 13.8165 20.2497 12.0002 20.25Z"
              fill="#3A383F"
            />
          </svg>
        </ButtonIcon>
        <ButtonIcon>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M15.7499 21C15.7499 21.1989 15.6709 21.3897 15.5303 21.5303C15.3896 21.671 15.1988 21.75 14.9999 21.75H8.99993C8.80102 21.75 8.61025 21.671 8.4696 21.5303C8.32895 21.3897 8.24993 21.1989 8.24993 21C8.24993 20.8011 8.32895 20.6103 8.4696 20.4697C8.61025 20.329 8.80102 20.25 8.99993 20.25H14.9999C15.1988 20.25 15.3896 20.329 15.5303 20.4697C15.6709 20.6103 15.7499 20.8011 15.7499 21ZM20.7984 18C20.6682 18.2292 20.4793 18.4196 20.2511 18.5514C20.0228 18.6833 19.7635 18.7518 19.4999 18.75H4.49993C4.23624 18.7496 3.97731 18.6798 3.74923 18.5475C3.52115 18.4151 3.33198 18.225 3.20079 17.9963C3.0696 17.7676 3.00102 17.5083 3.00196 17.2446C3.00291 16.9809 3.07335 16.7222 3.20618 16.4944C3.72649 15.5981 4.49993 13.0631 4.49993 9.75C4.49993 7.76088 5.29011 5.85322 6.69663 4.4467C8.10315 3.04018 10.0108 2.25 11.9999 2.25C13.9891 2.25 15.8967 3.04018 17.3032 4.4467C18.7098 5.85322 19.4999 7.76088 19.4999 9.75C19.4999 13.0622 20.2743 15.5981 20.7946 16.4944C20.9288 16.7225 20.9997 16.9822 21.0002 17.2468C21.0007 17.5114 20.9307 17.7714 20.7974 18H20.7984ZM19.4999 17.25C18.7752 16.0059 17.9999 13.1297 17.9999 9.75C17.9999 8.1587 17.3678 6.63258 16.2426 5.50736C15.1174 4.38214 13.5912 3.75 11.9999 3.75C10.4086 3.75 8.88251 4.38214 7.75729 5.50736C6.63207 6.63258 5.99993 8.1587 5.99993 9.75C5.99993 13.1306 5.22368 16.0069 4.49993 17.25H19.4999Z"
              fill="#3A383F"
            />
          </svg>
        </ButtonIcon> */}

        <div className={style.user_drop_down_container} ref={userDropDownRef}>
          <button
            className={style.user_drop_down}
            onClick={() => setUserDropDownOpen(!userDropDownOpen)}
          >
            <Avatar size="medium" name={loggedInUser?.name?.charAt(0)} />
            <div className={style.user_info}>
              <span>{loggedInUser?.name}</span>
              <span>{loggedInUser?.email}</span>
            </div>

            <svg
              className={`${style.user_drop_down_arrow} ${userDropDownOpen ? style.open : ""}`}
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                d="M13.3538 10.3538C13.3073 10.4003 13.2522 10.4371 13.1915 10.4623C13.1308 10.4875 13.0657 10.5004 13 10.5004C12.9343 10.5004 12.8693 10.4875 12.8086 10.4623C12.7479 10.4371 12.6927 10.4003 12.6463 10.3538L8.00003 5.7069L3.35378 10.3538C3.25996 10.4476 3.13272 10.5003 3.00003 10.5003C2.86735 10.5003 2.7401 10.4476 2.64628 10.3538C2.55246 10.26 2.49976 10.1327 2.49976 10C2.49976 9.86735 2.55246 9.7401 2.64628 9.64628L7.64628 4.64628C7.69272 4.59979 7.74786 4.56291 7.80856 4.53775C7.86926 4.51259 7.93433 4.49963 8.00003 4.49963C8.06574 4.49963 8.13081 4.51259 8.1915 4.53775C8.2522 4.56291 8.30735 4.59979 8.35378 4.64628L13.3538 9.64628C13.4003 9.69271 13.4372 9.74786 13.4623 9.80856C13.4875 9.86926 13.5004 9.93432 13.5004 10C13.5004 10.0657 13.4875 10.1308 13.4623 10.1915C13.4372 10.2522 13.4003 10.3073 13.3538 10.3538Z"
                fill="#3A383F"
              />
            </svg>
          </button>

          <div
            className={`${style.user_drop_down_body} ${userDropDownOpen ? style.open : ""}`}
          >
            <div className={style.drop_down_header}>
              <span className={style.drop_down_header_name}>
                {loggedInUser?.name}
              </span>
              <span className={style.drop_down_header_email}>
                {loggedInUser?.email}
              </span>
            </div>
            <div className={style.drop_down_item_list}>
              <div
                className={style.drop_down_item}
                onClick={() => {
                  history.push("/v2/profile/notification-settings");
                  setUserDropDownOpen(false);
                }}
              >
                <span>Notification settings</span>
              </div>

              <div
                className={style.drop_down_item}
                onClick={() => {
                  setPersonalDetailsModal(true);
                  setUserDropDownOpen(false);
                }}
              >
                <span>Change Personal Details</span>
              </div>

              <div
                className={style.drop_down_item}
                onClick={() => {
                  setResetPasswordModal(true);
                  setUserDropDownOpen(false);
                }}
              >
                <span>Reset Password</span>
              </div>

              <div
                className={style.drop_down_item}
                onClick={() => {
                  setApiTokensModal(true);
                  setUserDropDownOpen(false);
                }}
              >
                <span>API Tokens</span>
              </div>

              <div
                className={style.drop_down_item}
                onClick={() => {
                  setTwoFactorModal(true);
                  setUserDropDownOpen(false);
                }}
              >
                <span>Two-Factor Auth</span>
              </div>

              {loggedInUser?.is_impersonating && (
                <div
                  className={`${style.drop_down_item} ${style.drop_down_item_logout} `}
                  onClick={() => {
                    window.location.href = "/admin/user/switch/stop";
                  }}
                >
                  <span>Logout ( Switch )</span>
                </div>
              )}

              <div
                className={`${style.drop_down_item} ${style.drop_down_item_logout} `}
                onClick={() => {
                  Logout()
                    .then((response) => {
                      window.location = "/";
                      setUserDropDownOpen(false);
                    })
                    .catch((error) => {});
                }}
              >
                <span>Log Out</span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {emsBootModal && (
        <EMSBootModal
          isOpen={emsBootModal}
          close={() => setEmsBootModal(false)}
          user={loggedInUser}
        />
      )}

      <ResetPasswordModal
        isOpen={resetPasswordModal}
        onClose={() => setResetPasswordModal(false)}
      />

      <NewPasswordModal
        isOpen={newPasswordModal}
        onClose={() => setNewPasswordModal(false)}
      />

      <PersonalDetailsModal
        isOpen={personalDetailsModal}
        onClose={() => setPersonalDetailsModal(false)}
      />

      <ApiTokensModal
        isOpen={apiTokensModal}
        onClose={() => setApiTokensModal(false)}
      />

      <TwoFactorModal
        isOpen={twoFactorModal}
        onClose={() => setTwoFactorModal(false)}
      />
    </header>
  );
};

export default withRouter(Header);
